
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Mail, FileText, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const LendSmart = () => {
  const heroRef = useScrollAnimation({ threshold: 0.2 });
  const statsRef = useScrollAnimation({ threshold: 0.2 });
  const featuresRef = useScrollAnimation({ threshold: 0.2 });

  // Mobile detection for enhanced animations
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-cyan-50 py-20 lg:py-28 overflow-hidden" ref={heroRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12 items-center">
            <div className="max-w-2xl">
              {/* Mobile: motion animation, Desktop: CSS transition */}
              {isMobile ? (
                <motion.div
                  className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-2xl mb-6"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "backOut" }}
                >
                  <Activity className="text-blue-600" size={32} />
                </motion.div>
              ) : (
                <div className={`inline-flex items-center justify-center p-3 bg-blue-100 rounded-2xl mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>
                  <Activity className="text-blue-600" size={32} />
                </div>
              )}

              {isMobile ? (
                <motion.h1
                  className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
                >
                  LendSmart AI
                </motion.h1>
              ) : (
                <h1 className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.1s' : undefined }}>
                  LendSmart AI
                </h1>
              )}

              {isMobile ? (
                <motion.p
                  className="text-xl text-gray-600 mb-8 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                >
                  Streamline commercial lending processes with AI-powered intelligence. Automate loan origination, underwriting, and document processing to reduce cycle time by 75-85%.
                </motion.p>
              ) : (
                <p className={`text-xl text-gray-600 mb-8 leading-relaxed transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.2s' : undefined }}>
                  Streamline commercial lending processes with AI-powered intelligence. Automate loan origination, underwriting, and document processing to reduce cycle time by 75-85%.
                </p>
              )}

              {isMobile ? (
                <motion.div
                  className="flex flex-col sm:flex-row gap-4"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
                >
                  <Link to="/contact" className="px-8 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors text-center">
                    Request Demo
                  </Link>
                </motion.div>
              ) : (
                <div className={`flex flex-col sm:flex-row gap-4 transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.3s' : undefined }}>
                  <Link to="/contact" className="px-8 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors text-center">
                    Request Demo
                  </Link>
                </div>
              )}
            </div>

            {isMobile ? (
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                <div className="absolute inset-0 bg-blue-600 rounded-3xl rotate-3 opacity-10"></div>
                <img
                  src="/What-is-Fintech_Finanacial-technology.webp"
                  alt="Financial Documents Analysis"
                  className="relative rounded-3xl shadow-2xl border-4 border-white"
                />
              </motion.div>
            ) : (
              <div className={`relative transition-all duration-1000 ${heroRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: heroRef.isVisible ? '0.4s' : undefined }}>
                <div className="absolute inset-0 bg-blue-600 rounded-3xl rotate-3 opacity-10"></div>
                <img
                  src="/What-is-Fintech_Finanacial-technology.webp"
                  alt="Financial Documents Analysis"
                  className="relative rounded-3xl shadow-2xl border-4 border-white transform hover:-translate-y-2 transition-transform duration-500 hover:scale-[1.02]"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-gray-100" ref={statsRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {isMobile ? (
              <>
                <motion.div
                  className="p-6 rounded-xl bg-blue-50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-4xl font-bold text-blue-600 mb-2">75-85%</div>
                  <div className="text-gray-600">Reduction in Processing Time</div>
                </motion.div>
                <motion.div
                  className="p-6 rounded-xl bg-cyan-50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-4xl font-bold text-cyan-600 mb-2">60-80%</div>
                  <div className="text-gray-600">Automated Email Triage</div>
                </motion.div>
                <motion.div
                  className="p-6 rounded-xl bg-indigo-50"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="text-4xl font-bold text-indigo-600 mb-2">90%</div>
                  <div className="text-gray-600">Manual Data Entry Eliminated</div>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`p-6 rounded-xl bg-blue-50 hover:bg-blue-100 transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: statsRef.isVisible ? '0s' : undefined }}>
                  <div className="text-4xl font-bold text-blue-600 mb-2">75-85%</div>
                  <div className="text-gray-600">Reduction in Processing Time</div>
                </div>
                <div className={`p-6 rounded-xl bg-cyan-50 hover:bg-cyan-100 transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: statsRef.isVisible ? '0.1s' : undefined }}>
                  <div className="text-4xl font-bold text-cyan-600 mb-2">60-80%</div>
                  <div className="text-gray-600">Automated Email Triage</div>
                </div>
                <div className={`p-6 rounded-xl bg-indigo-50 hover:bg-indigo-100 transition-all duration-1000 ${statsRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: statsRef.isVisible ? '0.2s' : undefined }}>
                  <div className="text-4xl font-bold text-indigo-600 mb-2">90%</div>
                  <div className="text-gray-600">Manual Data Entry Eliminated</div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24" ref={featuresRef.ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {isMobile ? (
              <>
                <motion.h2
                  className="text-3xl font-bold text-gray-900 mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  Transform Commercial Lending Operations
                </motion.h2>
                <motion.p
                  className="text-lg text-gray-600"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  Comprehensive tools designed to accelerate commercial loan origination, underwriting, and approval workflows.
                </motion.p>
              </>
            ) : (
              <>
                <h2 className={`text-3xl font-bold text-gray-900 mb-4 transition-all duration-1000 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`}>Transform Commercial Lending Operations</h2>
                <p className={`text-lg text-gray-600 transition-all duration-1000 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.1s' : undefined }}>Comprehensive tools designed to accelerate commercial loan origination, underwriting, and approval workflows.</p>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {isMobile ? (
              <>
                <motion.div
                  className="p-8 border border-transparent rounded-2xl bg-white shadow-sm"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6">
                    <FileText size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Document Intelligence</h3>
                  <p className="text-gray-600">Advanced RAG-based analysis for credit agreements, policies, and loan documents. Extract key terms and detect conflicts automatically.</p>
                </motion.div>
                <motion.div
                  className="p-8 border border-transparent rounded-2xl bg-white shadow-sm"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center text-cyan-600 mb-6">
                    <Mail size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Email Automation</h3>
                  <p className="text-gray-600">Auto-categorize and route thousands of emails daily. Convert unstructured email threads into structured cases.</p>
                </motion.div>
                <motion.div
                  className="p-8 border border-transparent rounded-2xl bg-white shadow-sm"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
                  viewport={{ once: true }}
                >
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-6">
                    <Clock size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Workflow Orchestration</h3>
                  <p className="text-gray-600">End-to-end case management from origination to servicing. Track SLAs and automate status updates.</p>
                </motion.div>
              </>
            ) : (
              <>
                <div className={`p-8 border border-transparent hover:border-blue-300 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0s' : undefined }}>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                    <FileText size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Document Intelligence</h3>
                  <p className="text-gray-600">Advanced RAG-based analysis for credit agreements, policies, and loan documents. Extract key terms and detect conflicts automatically.</p>
                </div>
                <div className={`p-8 border border-transparent hover:border-cyan-300 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.1s' : undefined }}>
                  <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center text-cyan-600 mb-6 group-hover:scale-110 transition-transform">
                    <Mail size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Email Automation</h3>
                  <p className="text-gray-600">Auto-categorize and route thousands of emails daily. Convert unstructured email threads into structured cases.</p>
                </div>
                <div className={`p-8 border border-transparent hover:border-indigo-300 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300 ${featuresRef.isVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: featuresRef.isVisible ? '0.2s' : undefined }}>
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 mb-6 group-hover:scale-110 transition-transform">
                    <Clock size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Workflow Orchestration</h3>
                  <p className="text-gray-600">End-to-end case management from origination to servicing. Track SLAs and automate status updates.</p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-blue-900 text-white relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={{
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3) 0%, transparent 60%)',
            backgroundSize: '200% 200%',
          }}
        />
        <motion.div
          className="max-w-4xl mx-auto px-4 text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            className="text-3xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            Transform Your Commercial Lending with LendSmart AI
          </motion.h2>
          <motion.p
            className="text-xl text-blue-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            75-85% faster processing. Reduce manual work by 90%. Deploy in weeks.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "backOut" }}
            viewport={{ once: true }}
          >
            <motion.div
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-50 transition-all"
              >
                Request Demo
              </Link>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

    </div>
  );
};

export default LendSmart;
